import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import type { FacetItem } from '@app/src/pages/ResourceCollection'
import { comparePeriods } from '@app/src/utils/getOrderedPeriods'
import { spendClassificationOrder } from '@app/src/wf-constants'
import { FormControlLabel, Stack, Switch } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import Filter, { hasMultipleFilter } from './Filter'
import FilterFacetSelect from './FilterFacetSelect'
import type { FilterInterface } from './Filters'
import { useFiltersContext } from './Filters'
import FilterSection from './FilterSection'
import { Operators } from './useFilters'

const periodNamesSortFunction = (a: FacetItem, b: FacetItem) => {
  return comparePeriods(a.label, b.label)
}

const spendClassificationSortFunction = (a: FacetItem, b: FacetItem) => {
  return spendClassificationOrder.indexOf(a.label) - spendClassificationOrder.indexOf(b.label)
}

const getSpendPeriodEntry = (filter: FilterInterface | FilterInterface[] | undefined) => {
  if (!filter) {
    return undefined
  }
  return hasMultipleFilter(filter) ? filter[0] : filter
}

interface SpendFilterProps {
  spendFilterNamePrefix?: string
  defaultCollapsed?: boolean
}

const SpendFilter = ({ spendFilterNamePrefix, defaultCollapsed }: SpendFilterProps) => {
  const { formatMessage } = useIntl()
  const { filters, setQueryFilter } = useFiltersContext()

  const spendPeriodFilterName = `${spendFilterNamePrefix ? `${spendFilterNamePrefix}.` : ''}spends.periodName`
  const spendIntervalFilterName = `${spendFilterNamePrefix ? `${spendFilterNamePrefix}.` : ''}spends.spendClassification`
  const spendPeriodEntry = getSpendPeriodEntry(filters[spendPeriodFilterName])
  const spendPeriodValue = spendPeriodEntry?.value as string | undefined
  const hasSpendPeriod = Boolean(spendPeriodValue)
  const isMissingSpend = hasSpendPeriod && spendPeriodEntry?.operator === Operators.NotEqualTo

  const handleMissingSpendToggle = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!spendPeriodValue) {
      return
    }
    const operator = checked ? Operators.NotEqualTo : Operators.EqualTo
    setQueryFilter(spendPeriodFilterName, spendPeriodValue, operator)
  }

  return (
    <FilterSection
      label={formatMessage({ id: 'schemas.supplier.annualSpend.spend' })}
      defaultCollapsed={defaultCollapsed}
    >
      <Stack spacing={2}>
        <Filter operator={isMissingSpend ? Operators.NotEqualTo : Operators.EqualTo} name={spendPeriodFilterName}>
          {({ value, onChange, filterName }) => (
            <FilterFacetSelect
              size="small"
              multiple={false}
              facetsParam={{
                key: [FetchKey.ProviderFacets, filterName],
                endpoint: endpoints.providersWithFacets,
                facetsParam: [{ name: 'spends.periodName', isEnum: true }],
              }}
              filterName={filterName}
              disableAutoFilter
              onChange={(periodValue: string | null) => onChange(periodValue)}
              fieldLabel={formatMessage({ id: 'schemas.filter.spendPeriod' })}
              value={Array.isArray(value) ? value[0] : value}
              customSort={periodNamesSortFunction}
            />
          )}
        </Filter>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={isMissingSpend}
              onChange={handleMissingSpendToggle}
              disabled={!hasSpendPeriod}
            />
          }
          label={formatMessage({ id: 'schemas.filter.spendIsNotAdded' })}
          disabled={!hasSpendPeriod}
        />
        <Filter operator={Operators.In} name={spendIntervalFilterName}>
          {({ value, onChange, filterName }) => (
            <FilterFacetSelect
              size="small"
              multiple
              facetsParam={{
                key: [FetchKey.ProviderFacets, filterName],
                endpoint: endpoints.providersWithFacets,
                facetsParam: [{ name: 'spends.spendClassification', isEnum: true }],
              }}
              filterName={filterName}
              disableAutoFilter
              onChange={(intervalValue: string[]) => onChange(intervalValue)}
              fieldLabel={formatMessage({ id: 'schemas.filter.spendInterval' })}
              value={[value].flat()}
              customSort={spendClassificationSortFunction}
            />
          )}
        </Filter>
      </Stack>
    </FilterSection>
  )
}

export default SpendFilter
